import Component from "@glimmer/component";
import { service } from "@ember/service";
import { task, Task } from "ember-concurrency";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { shape, instanceOf, arrayOf } from "prop-types";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { t } from "ember-intl";
import Header from "glesys-controlpanel/components/g-modal/header";
import Body from "glesys-controlpanel/components/g-modal/body";
import Footer from "glesys-controlpanel/components/g-modal/footer";
import modalClose from "glesys-controlpanel/helpers/modal-close";
import GButton from "glesys-controlpanel/components/g-button";
import SegmentedButtons from "glesys-controlpanel/components/segmented-buttons";
import TaskForm from "glesys-controlpanel/components/task-form";
import AlertMessage from "glesys-controlpanel/components/alert-message";
import InstanceUser from "glesys-controlpanel/models/instance-user";
import InstanceDatabase from "glesys-controlpanel/models/instance-database";

const LEVELS = ["none", "read", "write"];

@forbidExtraArgs
export default class ModalsDatabaseEditUserPermissions extends Component {
  @service modal;
  @service intl;

  @arg(
    shape({
      user: instanceOf(InstanceUser).isRequired,
      databases: arrayOf(instanceOf(InstanceDatabase)).isRequired,
      task: instanceOf(Task).isRequired,
    }).isRequired,
  )
  params;

  @tracked levels = this.initialLevels();

  initialLevels() {
    let granted = new Map(
      (this.params.user.permissions ?? []).map((permission) => [
        permission.databaseid,
        permission.permission.toLowerCase(),
      ]),
    );

    return Object.fromEntries(
      this.params.databases.map((database) => [database.id, granted.get(database.id) ?? "none"]),
    );
  }

  get hasNoDatabases() {
    return this.params.databases.length === 0;
  }

  labelForLevel(level) {
    switch (level) {
      case "read":
        return this.intl.t("databases.details.users.permission.read");
      case "write":
        return this.intl.t("databases.details.users.permission.write");
      default:
        return this.intl.t("label.none");
    }
  }

  optionsFor = (databaseId) =>
    LEVELS.map((level) => ({
      value: level,
      label: this.labelForLevel(level),
      checked: this.levels[databaseId] === level,
    }));

  onChange = (levels) => (this.levels = { ...this.levels, ...levels });

  onSubmitTask = task(async () => {
    let permissions = Object.entries(this.levels)
      .filter(([, level]) => level !== "none")
      .map(([databaseid, level]) => ({ databaseid, permission: level.toUpperCase() }));

    await this.params.task.perform(this.params.user, permissions);
    this.modal.close();
  });

  <template>
    <Header>{{t "databases.details.users.edit-permissions.title" username=this.params.user.username}}</Header>
    <Body>
      {{#if this.hasNoDatabases}}
        <AlertMessage @type="info">{{t "databases.details.users.edit-permissions.empty"}}</AlertMessage>
      {{else}}
        <p class="text-gray-700 pb-6">
          {{t "databases.details.users.edit-permissions.description" username=this.params.user.username}}
        </p>
        <TaskForm id="edit-permissions-form" @onChange={{this.onChange}} @task={{this.onSubmitTask}}>
          <div class="divide-y divide-gray-200 border-t border-b border-gray-200">
            {{#each this.params.databases as |database|}}
              <div class="flex items-center gap-3 py-2" data-test-permission-row={{database.name}}>
                <span class="flex-1 text-sm text-gray-900 truncate" id="permission-{{database.id}}">
                  {{database.name}}
                </span>
                <div class="-mt-4 w-fit shrink-0">
                  <SegmentedButtons
                    aria-labelledby="permission-{{database.id}}"
                    @name={{database.id}}
                    @options={{this.optionsFor database.id}}
                  />
                </div>
              </div>
            {{/each}}
          </div>
        </TaskForm>
      {{/if}}
    </Body>
    <Footer>
      <GButton class="mr-2" data-test-cancel-button @color="gray" {{on "click" (modalClose)}}>
        {{t "button.cancel"}}
      </GButton>
      <GButton
        data-test-modal-database-edit-user-permissions-submit
        @type="submit"
        @color="blue"
        @loading={{this.onSubmitTask.isRunning}}
        @disabled={{this.hasNoDatabases}}
        form="edit-permissions-form"
      >
        {{t "button.save"}}
      </GButton>
    </Footer>
  </template>
}
